"use client";

import {useState} from "react";
import AppIcon from "@/app/components/atomic/AppIcon";
import ChevronIcon from "@/app/components/homepage/components/ChevronIcon";
import SectionTitle from "@/app/components/atomic/molecules/SectionTitle";
import {
    COMPARATOR_FAMILIES,
    COMPARATOR_PRODUCT_COUNT,
    ComparatorFamily,
    ComparatorLink,
} from "@/app/components/homepage/components/comparatorFamilies";

function LinkCard({link, color}: { link: ComparatorLink; color: ComparatorFamily["color"] }) {
    return (
        <a
            href={link.url}
            data-test="comparator-link"
            className="flex items-center gap-3 rounded-xl border border-transparent bg-white px-4 py-3 transition hover:border-rm-blue"
        >
            <span className={`flex size-8 shrink-0 items-center justify-center rounded-lg bg-${color}`}>
                <AppIcon type="regular" iconClassName={link.icon} className="text-pale" size="sm" />
            </span>

            <span className="grow text-sm text-pale">{link.name}</span>

            <AppIcon type="regular" iconClassName="fa-chevron-right" className="text-rm-gray" size="xs" />
        </a>
    );
}

function FamilyIcon({family, className}: { family: ComparatorFamily; className: string }) {
    return (
        <span className={`flex shrink-0 items-center justify-center rounded-xl bg-${family.color} ${className}`}>
            <AppIcon type="regular" iconClassName={family.icon} className="text-pale" size="xl" />
        </span>
    );
}

export default function ComparatorTabs() {
    // Null rather than the first family: mobile opens fully collapsed, desktop still falls back to a selected tab
    const [openSlug, setOpenSlug] = useState<string | null>(null);
    const activeFamily = COMPARATOR_FAMILIES.find((family) => family.slug === openSlug) ?? COMPARATOR_FAMILIES[0];

    return (
        <div id="comparators" className="flex w-full max-w-[1200px] scroll-mt-24 flex-col gap-6 pt-8 pb-12 lg:pb-20 lg:pt-14">
            <div className="flex flex-col gap-2">
                <SectionTitle level="h2" theme="neutral">Toutes nos assurances</SectionTitle>

                <p className="text-sm text-rm-gray-dark">
                    {COMPARATOR_PRODUCT_COUNT} produits comparés, 100 % gratuitement
                    <span className="hidden lg:inline"> &amp; sans engagement</span>.
                </p>
            </div>

            <div
                role="tablist"
                aria-label="Familles de comparateurs"
                data-test="comparator-tabs"
                className="-mx-2 hidden gap-3 overflow-x-auto px-2 pb-1 lg:flex"
            >
                {COMPARATOR_FAMILIES.map((family) => {
                    const isActive = family.slug === activeFamily.slug;

                    return (
                        <button
                            key={family.slug}
                            type="button"
                            role="tab"
                            id={`comparator-tab-${family.slug}`}
                            aria-selected={isActive}
                            aria-controls="comparator-panel"
                            onClick={() => setOpenSlug(family.slug)}
                            data-test="comparator-tab"
                            className={`flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm transition ${
                                isActive ? "bg-rm-blue text-white" : "bg-neutral text-pale hover:brightness-95"
                            }`}
                        >
                            <span className={`size-2 shrink-0 rounded-full bg-${family.color}`} />
                            {family.name}
                            <span className={isActive ? "text-white/60" : "text-rm-gray"}>{family.links.length}</span>
                        </button>
                    );
                })}
            </div>

            <div
                role="tabpanel"
                id="comparator-panel"
                aria-labelledby={`comparator-tab-${activeFamily.slug}`}
                data-test="comparator-panel"
                className="hidden gap-6 rounded-2xl bg-neutral p-5 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-10 lg:p-8"
            >
                <div className="flex flex-col gap-3">
                    <FamilyIcon family={activeFamily} className="size-12" />

                    <h3 className="inline-block px-0 text-2xl text-pale">{activeFamily.name}</h3>

                    <p className="text-sm leading-relaxed text-rm-gray-dark">{activeFamily.description}</p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                    {activeFamily.links.map((link) => (
                        <LinkCard key={link.url} link={link} color={activeFamily.color} />
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-3 lg:hidden" data-test="comparator-accordion">
                {COMPARATOR_FAMILIES.map((family) => {
                    const isOpen = family.slug === openSlug;

                    return (
                        <div key={family.slug} className="rounded-2xl bg-neutral p-3">
                            <button
                                type="button"
                                aria-expanded={isOpen}
                                aria-controls={`comparator-family-${family.slug}`}
                                onClick={() => setOpenSlug(isOpen ? null : family.slug)}
                                data-test="comparator-family"
                                className="flex w-full cursor-pointer items-center gap-3 text-left"
                            >
                                <FamilyIcon family={family} className="size-11" />

                                <span className="grow">
                                    <h3 className="inline-block px-0 text-lg text-pale">{family.name}</h3>
                                    <span className="block text-sm text-rm-gray-dark">{family.links.length} produits</span>
                                </span>

                                <ChevronIcon open={isOpen} className="mr-2 text-pale" />
                            </button>

                            {isOpen && (
                                <div id={`comparator-family-${family.slug}`} className="flex flex-col gap-2 pt-3">
                                    {family.links.map((link) => (
                                        <LinkCard key={link.url} link={link} color={family.color} />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
